import { memo } from 'react'
import { Users } from 'lucide-react'
import { clsx } from 'clsx'
import { Checkbox } from '@/components/ui/Checkbox'
import { isOverdue, formatDueDate } from '@/lib/dateUtils'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'
import type { UnifiedTask } from '@/lib/sharedTypes'

interface SharedTaskInlineItemProps {
  task: UnifiedTask
  onToggle: () => void
  onClick: () => void
}

export const SharedTaskInlineItem = memo(function SharedTaskInlineItem({
  task,
  onToggle,
  onClick,
}: SharedTaskInlineItemProps) {
  const isCompleted = task.status === 'completed'
  const overdue = !isCompleted && task.dueDate && isOverdue(task.dueDate)

  // 낙관적 토글 전송 중 여부 (states#6) — 전송 중이면 살짝 흐리게 표시
  const isPending = useSharedGroupStore((s) =>
    task.sharedTask ? s._pendingTaskToggles.has(task.sharedTask.id) : false
  )

  return (
    <div
      className={clsx(
        'flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all cursor-pointer',
        isCompleted
          ? 'bg-surface-secondary border-base opacity-60'
          : 'bg-surface-primary border-base hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-e1',
        isPending && 'opacity-60'
      )}
      onClick={onClick}
    >
      <div onClick={(e) => { e.stopPropagation(); onToggle() }}>
        <Checkbox checked={isCompleted} onChange={onToggle} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={clsx(
              'text-sm truncate',
              isCompleted ? 'line-through text-disabled' : 'text-heading font-medium'
            )}
          >
            {task.title}
          </span>
        </div>

        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {/* 공유 카테고리 배지 */}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-meta font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
            공유
          </span>

          {/* 그룹 배지 */}
          {task.groupName && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-meta font-medium text-white"
              style={{ backgroundColor: task.groupColor || 'var(--color-primary-500)' }}
            >
              <Users className="w-3 h-3" />
              {task.groupName}
            </span>
          )}

          {/* 담당자 (복수 지원) */}
          {task.assigneeNames && task.assigneeNames.length > 0 ? (
            <span className="text-meta text-caption">
              {task.assigneeNames.length > 2
                ? `${task.assigneeNames[0]} 외 ${task.assigneeNames.length - 1}명`
                : task.assigneeNames.join(', ')}
            </span>
          ) : task.assigneeName ? (
            <span className="text-meta text-caption">
              {task.assigneeName}
            </span>
          ) : null}

          {/* 마감일 */}
          {task.dueDate && (
            <span
              className={clsx(
                'text-meta',
                overdue ? 'text-red-500 font-medium' : 'text-caption'
              )}
            >
              {formatDueDate(task.dueDate)}
            </span>
          )}

          {/* 우선순위 — 테마 적응형 priority 토큰 (카테고리처럼 overlay 리틴팅 상속) */}
          {task.priority && task.priority !== 'none' && (
            <span
              className="text-meta font-medium"
              style={{
                color:
                  task.priority === 'high'
                    ? 'var(--priority-high)'
                    : task.priority === 'medium'
                      ? 'var(--priority-medium)'
                      : 'var(--priority-low)',
              }}
            >
              {task.priority === 'high' ? '높음' : task.priority === 'medium' ? '중간' : '낮음'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
})
